import React, { useEffect, useState } from "react";
import { Upload, Save, Building2, FileText } from "lucide-react";

/**
 * ProfiloAzienda
 * - GET  /api/azienda_profile_get.php
 * - POST /api/azienda_profile_update.php   (JSON)
 * - POST /api/azienda_logo_upload.php     (FormData: logo)
 *
 * Props:
 * - me: oggetto da /api/me.php
 * - canEdit: boolean (admin/superadmin)
 */
export default function ProfiloAzienda({ me, canEdit }) {
    const [loading, setLoading] = useState(true);
    const [azienda, setAzienda] = useState(null);
    const [docStatus, setDocStatus] = useState(null);
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setMsg(null);

        fetch("/api/azienda_profile_get.php", { credentials: "include" })
            .then((r) => r.json())
            .then((j) => {
                if (!alive) return;
                if (j?.ok) setAzienda(j.azienda);
                else setMsg(j?.error || "Errore caricamento profilo azienda");
            })
            .catch(() => alive && setMsg("Errore di rete"))
            .finally(() => alive && setLoading(false));

        fetch("/api/azienda_profile_status.php", { credentials: "include" })
            .then((r) => r.json())
            .then((j) => {
                if (!alive) return;
                if (j?.ok) setDocStatus(j.documenti_amministrativi || null);
            })
            .catch(() => {});

        return () => {
            alive = false;
        };
    }, []);

    function setField(key, value) {
        setAzienda((a) => ({ ...(a || {}), [key]: value }));
    }

    async function onSave() {
        if (!canEdit) return;

        setSaving(true);
        setMsg(null);

        const payload = {
            nome: azienda?.nome || "",
            indirizzo: azienda?.indirizzo || "",
            partita_iva: azienda?.partita_iva || "",
            cap: azienda?.cap || "",
            comune: azienda?.comune || "",
            provincia: azienda?.provincia || "",
            codice_univoco: azienda?.codice_univoco || "",
            pec: azienda?.pec || "",

            referente_nome: azienda?.referente_nome || "",
            referente_cell: azienda?.referente_cell || "",
            referente_email: azienda?.referente_email || "",

            haccp_resp_nome: azienda?.haccp_resp_nome || "",
            haccp_resp_cognome: azienda?.haccp_resp_cognome || "",
            haccp_resp_ruolo: azienda?.haccp_resp_ruolo || "",
            haccp_resp_cell: azienda?.haccp_resp_cell || "",
            haccp_resp_email: azienda?.haccp_resp_email || "",
            haccp_resp_is_referente: Number(azienda?.haccp_resp_is_referente || 0),
        };

        try {
            const r = await fetch("/api/azienda_profile_update.php", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const j = await r.json();
            if (j?.ok) setMsg("✅ Profilo azienda aggiornato");
            else setMsg(j?.error || "Errore salvataggio");
        } catch (e) {
            setMsg("Errore di rete");
        } finally {
            setSaving(false);
        }
    }

    async function onLogoChange(file) {
        if (!canEdit || !file) return;

        // (opzionale) check lato client
        const maxBytes = 2 * 1024 * 1024;
        if (file.size > maxBytes) {
            setMsg("Il file è troppo grande (max 2MB).");
            return;
        }

        setLogoUploading(true);
        setMsg(null);

        const fd = new FormData();
        fd.append("logo", file);

        try {
            const r = await fetch("/api/azienda_logo_upload.php", {
                method: "POST",
                credentials: "include",
                body: fd,
            });
            const j = await r.json();
            if (j?.ok) {
                setAzienda((a) => ({ ...(a || {}), logo_path: j.logo_path }));
                setMsg("✅ Logo aggiornato");
            } else {
                setMsg(j?.error || "Errore upload logo");
            }
        } catch (e) {
            setMsg("Errore di rete");
        } finally {
            setLogoUploading(false);
        }
    }

    if (loading) return <div className="p-4">Caricamento profilo…</div>;

    return (
        <div className="bg-white rounded-2xl shadow-sm border p-4 md:p-6">
            {/* Header card */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                        <Building2 size={18} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900">Profilo Azienda</h2>
                        <p className="text-sm text-slate-600">
                            {canEdit
                                ? "Puoi modificare i dati e caricare il logo."
                                : "Sei in sola lettura (solo admin/superadmin possono modificare)."}
                        </p>
                    </div>
                </div>

                {/* Logo box + upload */}
                <div className="flex items-center gap-3">
                    <div className="h-14 w-14 rounded-xl border bg-slate-50 flex items-center justify-center overflow-hidden">
                        {azienda?.logo_path ? (
                            <img
                                src={`/${azienda.logo_path}`}
                                alt="Logo azienda"
                                className="h-full w-full object-contain"
                            />
                        ) : (
                            <span className="text-xs text-slate-500">Logo</span>
                        )}
                    </div>

                    <label
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm
              ${canEdit
                                ? "cursor-pointer hover:bg-slate-50"
                                : "opacity-50 cursor-not-allowed"
                            }`}
                        title={!canEdit ? "Solo admin/superadmin" : ""}
                    >
                        <Upload size={16} />
                        {logoUploading ? "Caricamento…" : "Carica logo"}
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            disabled={!canEdit}
                            onChange={(e) => onLogoChange(e.target.files?.[0])}
                        />
                    </label>
                </div>
            </div>

            {/* Message */}
            {msg && (
                <div className="mt-4 text-sm px-3 py-2 rounded-lg bg-slate-50 border">
                    {msg}
                </div>
            )}

            {/* Form */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 rounded-2xl border border-sky-200 bg-sky-50 p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-sm font-bold text-sky-950">Documenti amministrativi / autorizzativi</div>
                            <div className="mt-1 text-sm text-sky-900">
                                Carica qui visura camerale, SCIA, notifica sanitaria, planimetrie, carta d'identita del legale rappresentante e autorizzazioni da usare anche nel Panic Mode ispettore.
                            </div>
                            {docStatus && (
                                <div className="mt-2 text-xs font-semibold text-sky-950">
                                    Checklist obbligatoria: {docStatus.required_present}/{docStatus.required_total} presenti
                                    {docStatus.required_missing > 0 ? ` · ${docStatus.required_missing} mancanti` : " · completa"}
                                </div>
                            )}
                        </div>
                        <a
                            href="/api/documenti_azienda.php?cat=documenti_amministrativi"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 rounded-xl border border-sky-300 bg-white px-4 py-2 text-sm font-semibold text-sky-950 hover:bg-sky-100"
                        >
                            <FileText size={16} />
                            Apri archivio documenti
                        </a>
                    </div>

                    {docStatus?.checklist?.length > 0 && (
                        <div className="mt-4 grid gap-2 md:grid-cols-2">
                            {docStatus.checklist.map((item) => (
                                <div
                                    key={item.key}
                                    className={`rounded-xl border p-3 text-sm ${
                                        item.present
                                            ? "border-emerald-200 bg-emerald-50"
                                            : item.required
                                                ? "border-rose-200 bg-rose-50"
                                                : "border-amber-200 bg-amber-50"
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="font-semibold text-slate-900">{item.label}</div>
                                        <div className={`rounded-full px-2 py-1 text-xs font-bold ${
                                            item.present
                                                ? "bg-emerald-100 text-emerald-800"
                                                : item.required
                                                    ? "bg-rose-100 text-rose-800"
                                                    : "bg-amber-100 text-amber-800"
                                        }`}>
                                            {item.present ? "Presente" : item.required ? "Mancante" : "Opzionale"}
                                        </div>
                                    </div>
                                    {item.matched_files?.length > 0 && (
                                        <div className="mt-2 text-xs text-slate-600">
                                            File trovati: {item.matched_files.join(", ")}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <Field
                    label="Nome azienda"
                    value={azienda?.nome || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("nome", v)}
                />
                <Field
                    label="Partita IVA"
                    value={azienda?.partita_iva || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("partita_iva", v)}
                />

                <Field
                    label="Indirizzo"
                    value={azienda?.indirizzo || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("indirizzo", v)}
                />
                <Field
                    label="CAP"
                    value={azienda?.cap || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("cap", v)}
                />

                <Field
                    label="Comune"
                    value={azienda?.comune || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("comune", v)}
                />
                <Field
                    label="Provincia"
                    value={azienda?.provincia || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("provincia", v)}
                />

                <Field
                    label="Codice Univoco"
                    value={azienda?.codice_univoco || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("codice_univoco", v)}
                />
                <Field
                    label="PEC"
                    value={azienda?.pec || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("pec", v)}
                />

                {/* Referente */}
                <div className="md:col-span-2 mt-2 pt-4 border-t">
                    <h3 className="font-semibold text-slate-900">Referente</h3>
                </div>

                <Field
                    label="Nome referente"
                    value={azienda?.referente_nome || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("referente_nome", v)}
                />
                <Field
                    label="Cellulare referente"
                    value={azienda?.referente_cell || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("referente_cell", v)}
                />
                <Field
                    label="Email referente"
                    value={azienda?.referente_email || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("referente_email", v)}
                />

                {/* HACCP */}
                <div className="md:col-span-2 mt-2 pt-4 border-t">
                    <h3 className="font-semibold text-slate-900">Responsabile HACCP</h3>
                </div>

                <Field
                    label="Nome"
                    value={azienda?.haccp_resp_nome || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("haccp_resp_nome", v)}
                />
                <Field
                    label="Cognome"
                    value={azienda?.haccp_resp_cognome || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("haccp_resp_cognome", v)}
                />
                <Field
                    label="Ruolo"
                    value={azienda?.haccp_resp_ruolo || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("haccp_resp_ruolo", v)}
                />
                <Field
                    label="Cellulare"
                    value={azienda?.haccp_resp_cell || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("haccp_resp_cell", v)}
                />
                <Field
                    label="Email"
                    value={azienda?.haccp_resp_email || ""}
                    disabled={!canEdit}
                    onChange={(v) => setField("haccp_resp_email", v)}
                />

                <div className="md:col-span-2 flex items-center justify-between gap-3 mt-2">
                    <label
                        className={`flex items-center gap-2 text-sm ${!canEdit ? "opacity-60" : ""
                            }`}
                    >
                        <input
                            type="checkbox"
                            checked={Number(azienda?.haccp_resp_is_referente || 0) === 1}
                            disabled={!canEdit}
                            onChange={(e) =>
                                setField("haccp_resp_is_referente", e.target.checked ? 1 : 0)
                            }
                        />
                        Il responsabile HACCP coincide col referente
                    </label>

                    <button
                        onClick={onSave}
                        disabled={!canEdit || saving}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
              ${canEdit
                                ? "bg-slate-900 text-white hover:bg-slate-800"
                                : "bg-slate-200 text-slate-500"
                            }
              ${saving ? "opacity-70" : ""}`}
                    >
                        <Save size={16} />
                        {saving ? "Salvataggio…" : "Salva modifiche"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, disabled }) {
    return (
        <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">{label}</div>
            <input
                className="w-full rounded-lg border px-3 py-2 text-sm bg-white disabled:bg-slate-50"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );
}
